import { Button } from 'components/Button';
import { Heading } from 'components/Heading';
import { Section } from 'components/Section';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './Tools.module.css';
import { rigs } from './rigs';
import { tools } from './toolsData';
import { useToolSound } from './useToolSound';

// Measured against the reference design: the opened icon scales up and parks
// left of the card, and its siblings slide outward from it.
const SPLAY_STEP = 300;
const ZOOM = 1.75;
// The smallest imbalance above and below worth moving the section for.
const REBALANCE_MIN = 12;
// How far the page can move before an open card closes itself.
const SCROLL_TO_CLOSE = 80;
// Clear space between the fade and the card, so nothing slides under it.
const CARD_GAP = 32;
// Below this the cluster wraps and the card is a centred overlay, so there is
// no side-by-side layout to fit.
const WIDE = '(min-width: 901px)';
// Wrapped on a phone, full-size stickers stack one or two to a row and the
// section runs to three screens.
const NARROW_SCALE = 0.55;
// The row's width at full size: every sticker, less each overlap.
const ROW_WIDTH = tools.reduce((sum, tool) => sum + tool.width - tool.lap, 0);
// Where the row meets its own next copy when stepping loops round, so the
// last sticker and the first overlap like any other neighbours.
const WRAP_LAP = 60;
// The fastest the arrow keys may step. Held down, a key repeats about thirty
// times a second, which spun the row faster than it could be read.
const STEP_EVERY = 220;

/* Open, the row is a ring: each sticker takes the slot nearest the open one
   going either way round, so stepping past the end brings the far end round
   as the next neighbour instead of sending the whole row back. Returns that
   slot, and which copy of the row it sits in (-1, 0 or 1). */
function ringSlot(index, openIndex) {
  const n = tools.length;
  const half = Math.floor(n / 2);
  const slot = ((((index - openIndex) % n) + n + half) % n) - half;
  return { slot, copy: (slot - (index - openIndex)) / n };
}

export function ToolFamily({ id, sectionRef, visible, ...rest }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [focusIndex, setFocusIndex] = useState(-1);
  const { play, muted, setMuted } = useToolSound();

  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const cardRef = useRef(null);
  const itemRefs = useRef([]);
  const lastStep = useRef(0);
  const lastLane = useRef(tools.map((_, index) => index));
  // Where the open card is in the endless row: counts on past either end
  // instead of wrapping, so every sticker's lane stays put while stepping.
  const cursor = useRef(null);
  const lastCursor = useRef(null);

  const isOpen = openIndex !== null;
  const router = useRouter();

  /* Park the opened icon in the middle of the room left of the card, and
     fade the cluster out before it reaches the card.

     Rest positions come from offsetLeft, which transforms do not touch. They
     used to be read from getBoundingClientRect on every step, while the icons
     were already shifted and splayed, so each step started from the last
     step's wrong answer and the active icon drifted across the screen.

     Open, the stage is pinned to the parking spot and every sticker is placed
     from there, in a row that repeats either side (see ringSlot). Pinning the
     stage matters for the loop: when it followed the open sticker, stepping
     from the last to the first slid the whole row a row's width.

     A sticker whose lane in that repeating row changes is jumping to the other
     end. Sliding it there would streak it across everything, so it moves while
     hidden (data-jump) and fades back in. Runs before paint so the jump is
     never drawn.

     Written straight to the DOM rather than held in state: these are purely
     presentational offsets, and state would re-render the cluster for
     something CSS can apply on its own. */
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const stage = stageRef.current;
    if (!viewport || !stage) return undefined;
    let frame;

    const place = () => {
      const wide = window.matchMedia(WIDE).matches;
      /* Shrink the whole row to fit beside the nav. Seven stickers at full
         size are wider than a laptop window leaves, and the ends ran under
         the nav. Scaling the sizes rather than transforming the stage keeps
         offsetLeft true for the maths below. */
      const fit = wide ? Math.min(1, viewport.clientWidth / ROW_WIDTH) : NARROW_SCALE;
      stage.style.setProperty('--k', String(fit));

      const items = itemRefs.current;
      const card = cardRef.current;
      const n = tools.length;
      const parked = openIndex !== null && card && wide;
      const copyOf = lane => Math.floor(lane / n);

      // Follow the open card round the ring. Steps are always one sticker, so
      // the shortest way round from the last position is the step taken.
      if (openIndex === null) cursor.current = null;
      else if (cursor.current === null) cursor.current = openIndex + copyOf(lastCursor.current ?? 0) * n;
      else {
        const from = ((cursor.current % n) + n) % n;
        cursor.current += ringSlot(openIndex, from).slot;
      }
      const at = cursor.current;
      // Closed, each sticker sits in its own lane of the copy the cursor
      // last stopped in.
      const home = index => index + copyOf(lastCursor.current ?? 0) * n;
      const centre = items.map(el => el.offsetLeft + el.offsetWidth / 2);
      // How far the row's next copy starts from this one.
      const period =
        centre[n - 1] - centre[0] + (items[n - 1].offsetWidth + items[0].offsetWidth) / 2 - WRAP_LAP * fit;

      if (parked) {
        const box = viewport.getBoundingClientRect();
        // The card's resting left edge in the window, read past its entry
        // animation, which scales it.
        const room = card.offsetParent.getBoundingClientRect().left + card.offsetLeft - CARD_GAP;
        // The transformed stage is the icons' offsetParent, so its own
        // (untransformed) offset within the viewport is taken back out.
        stage.style.setProperty('--shift', `${(box.left + room) / 2 - box.left - stage.offsetLeft}px`);
        viewport.style.setProperty('--clipRight', `${Math.max(0, box.right - room)}px`);
      } else {
        stage.style.setProperty('--shift', '0px');
        viewport.style.setProperty('--clipRight', '0px');
      }

      let jumped = false;
      items.forEach((el, index) => {
        let splay = 0;
        let lane = home(index);
        if (parked) {
          const { slot } = ringSlot(index, openIndex);
          lane = at + slot;
          splay = slot * SPLAY_STEP * fit + (copyOf(lane) - copyOf(at)) * period - centre[openIndex];
        } else if (openIndex !== null) {
          // Narrow: the card covers the row, which just steps back behind it.
          splay = (index - openIndex) * SPLAY_STEP * fit;
        }
        if (lane !== lastLane.current[index]) {
          el.setAttribute('data-jump', '');
          jumped = true;
        }
        lastLane.current[index] = lane;
        el.style.setProperty('--splay', `${splay}px`);
      });

      if (at !== null) lastCursor.current = at;

      if (jumped) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          frame = requestAnimationFrame(() => {
            items.forEach(el => el.removeAttribute('data-jump'));
          });
        });
      }
    };

    place();
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
    };
  }, [openIndex]);

  const open = useCallback(
    index => {
      setOpenIndex(index);
      setFocusIndex(index);
      play('open');
    },
    [play]
  );

  const close = useCallback(() => {
    setOpenIndex(current => {
      if (current === null) return current;
      play('close');
      return null;
    });
  }, [play]);

  // A click lands the keyboard focus on a sticker, which shows its label, so a
  // click anywhere else has to take it away again, as Escape does.
  useEffect(() => {
    if (focusIndex < 0) return;
    const onPointerDown = event => {
      if (event.target.closest(`.${styles.item}, .${styles.card}`)) return;
      setFocusIndex(-1);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [focusIndex]);

  // One step per STEP_EVERY, however fast the key repeats.
  const throttled = () => {
    const now = performance.now();
    if (now - lastStep.current < STEP_EVERY) return true;
    lastStep.current = now;
    return false;
  };

  const step = useCallback(
    delta => {
      if (throttled()) return;
      setOpenIndex(current => {
        if (current === null) return current;
        const next = (current + delta + tools.length) % tools.length;
        setFocusIndex(next);
        play(delta > 0 ? 'forward' : 'back');
        return next;
      });
    },
    [play]
  );

  const moveFocus = useCallback(
    delta => {
      if (throttled()) return;
      setFocusIndex(current => {
        const next = (current + delta + tools.length) % tools.length;
        itemRefs.current[next]?.focus({ preventScroll: true });
        play('tap');
        return next;
      });
    },
    [play]
  );

  // Only bind keys while this section is the one being looked at, so the
  // homepage's other sections keep their own arrow-key behaviour.
  useEffect(() => {
    if (!visible && !isOpen) return undefined;

    const onKeyDown = event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (isOpen) {
        if (event.key === 'Escape' || event.key === ' ') {
          event.preventDefault();
          close();
        } else if (event.key === 'Enter') {
          // Enter went in; Enter again goes through to the project page, or
          // to the repo for a project that has no page yet.
          event.preventDefault();
          const { href } = tools[openIndex];
          // A private project has nowhere to go.
          if (!href) return;
          if (/^https?:/.test(href)) window.open(href, '_blank', 'noopener');
          else router.push(href);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
        return;
      }

      if (focusIndex < 0) return; // not engaged with the cluster yet

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveFocus(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveFocus(-1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open(focusIndex);
      } else if (event.key === 'Escape') {
        setFocusIndex(-1);
        document.activeElement?.blur?.();
        play('release');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, isOpen, openIndex, focusIndex, close, step, moveFocus, open, play, router]);

  /* The rigs' eyes follow the pointer: each SVG takes a gaze direction in
     [-1, 1] and looks there until the pointer rests for two seconds. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let idle;
    const onMove = event => {
      for (const svg of stage.querySelectorAll('.totem-rig')) {
        const rect = svg.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        const d = Math.hypot(dx, dy) || 1;
        const k = Math.min(1, d / 280) / d;
        svg.style.setProperty('--gaze-x', (dx * k).toFixed(3));
        svg.style.setProperty('--gaze-y', (dy * k).toFixed(3));
        svg.classList.add('gazing');
      }
      clearTimeout(idle);
      idle = setTimeout(() => {
        stage.querySelectorAll('.totem-rig').forEach(svg => svg.classList.remove('gazing'));
      }, 2000);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      clearTimeout(idle);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  /* Scrolling away puts the row back. The card belongs to this section, so
     once the reader has moved on it should not still be open when they come
     back. A small allowance so a trackpad settling does not count. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const from = window.scrollY;
    const onScroll = () => {
      if (Math.abs(window.scrollY - from) > SCROLL_TO_CLOSE) close();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isOpen, close]);

  /* Sit visually halfway between the sections either side.

     The section above is a full screen with a 3D laptop centred in it, drawn
     on a canvas taller than the laptop. How much empty canvas trails below
     the laptop depends on the window's width and height together, so no
     fixed padding could even this out: it measured anywhere from 40px to
     490px. Instead, measure what the neighbours actually draw, skipping the
     canvas, and move this section until the gap above matches the gap below. */
  useEffect(() => {
    const section = viewportRef.current?.closest('section');
    if (!section) return undefined;

    // Only what paints: leaves, plus containers with a background or border
    // of their own. A container's box can run well past what it draws; the
    // lettering under the laptop is an <svg> whose box ends 76px below its
    // glyphs.
    // A 3D device canvas is far taller than the device drawn on it, so it
    // counts only down to where the device ends, which the model reports.
    const paints = node => {
      if (node.childElementCount === 0) return true;
      // The site's buttons draw their fill in a pseudo-element, which a walk
      // of the DOM never reaches, so a button counts by its whole box.
      if (node.tagName === 'A' || node.tagName === 'BUTTON') return true;
      const style = getComputedStyle(node);
      return (
        !/rgba\(.*,\s*0\)|transparent/.test(style.backgroundColor) ||
        style.backgroundImage !== 'none' ||
        parseFloat(style.borderTopWidth) > 0 ||
        parseFloat(style.borderBottomWidth) > 0
      );
    };
    const drawn = el =>
      [...el.querySelectorAll('*')]
        .filter(paints)
        .map(node => {
          const rect = node.getBoundingClientRect();
          if (node.tagName === 'CANVAS') return canvasRect(node, rect);
          // A line box has space above the capitals and below the baseline;
          // trim it so a heading and a hint line are measured by their
          // letters, as they are seen.
          if (node.childElementCount === 0 && node.textContent.trim()) {
            const style = getComputedStyle(node);
            const size = parseFloat(style.fontSize);
            const line = parseFloat(style.lineHeight) || size * 1.2;
            const trim = (line - size) / 2 + size * 0.2;
            return { top: rect.top + trim, bottom: rect.bottom - trim, width: rect.width, height: rect.height };
          }
          return rect;
        })
        .filter(rect => rect && rect.width > 0 && rect.height > 0);

    function canvasRect(node, rect) {
      const bottom = Number(node.dataset.drawnBottom);
      if (!Number.isFinite(bottom)) return null;
      return { top: rect.top, bottom: rect.top + bottom * rect.height, width: rect.width, height: rect.height };
    }

    const balance = () => {
      const above = section.previousElementSibling;
      const below = section.nextElementSibling;
      if (!above || !below || !window.matchMedia(WIDE).matches) {
        section.style.marginTop = '';
        return;
      }

      const aboveEnd = Math.max(...drawn(above).map(rect => rect.bottom));
      const belowStart = Math.min(...drawn(below).map(rect => rect.top));
      const own = drawn(section);
      const ownStart = Math.min(...own.map(rect => rect.top));
      const ownEnd = Math.max(...own.map(rect => rect.bottom));
      if (![aboveEnd, belowStart, ownStart, ownEnd].every(Number.isFinite)) return;

      // Moving this section carries the one below along with it, so the gap
      // below is fixed; bring the gap above to match it.
      const gapAbove = ownStart - aboveEnd;
      const gapBelow = belowStart - ownEnd;
      // Adjusted from where it is now, and only for a real difference: the
      // laptop leans with the pointer, which moves its reported base by a
      // few pixels, and chasing that would nudge the page as the mouse moves.
      if (Math.abs(gapBelow - gapAbove) < REBALANCE_MIN) return;
      const current = parseFloat(section.style.marginTop) || 0;
      section.style.marginTop = `${Math.round(current + gapBelow - gapAbove)}px`;
    };

    balance();
    const observer = new ResizeObserver(balance);
    observer.observe(document.body);
    window.addEventListener('modeldrawn', balance);
    return () => {
      observer.disconnect();
      window.removeEventListener('modeldrawn', balance);
    };
    // Again once visible: the sections either side animate in, and measuring
    // them mid-entrance reads them offset.
  }, [visible]);

  const active = isOpen ? tools[openIndex] : null;

  return (
    <Section
      className={styles.section}
      as="section"
      ref={sectionRef}
      id={id}
      data-open={isOpen || undefined}
      data-visible={visible || undefined}
      aria-labelledby="tools-title"
      {...rest}
    >
      <Heading
        level={4}
        as="h2"
        align="center"
        id="tools-title"
        className={styles.title}
      >
        More projects
      </Heading>

      <div className={styles.viewport} ref={viewportRef}>
      <div className={styles.stage} ref={stageRef}>
        {tools.map((tool, index) => {
          const isActive = openIndex === index;
          return (
            <button
              key={tool.id}
              type="button"
              // Out of the tab order on purpose: arrow keys are the way in.
              tabIndex={-1}
              ref={el => {
                itemRefs.current[index] = el;
              }}
              className={styles.item}
              data-focus={focusIndex === index || undefined}
              data-active={openIndex === index || undefined}
              style={{
                // All scaled by --k, which the stage sets so the row fits.
                '--w': `calc(${tool.width}px * var(--k, 1))`,
                '--dy': `calc(${tool.offset}px * var(--k, 1))`,
                '--lap': `calc(${tool.lap}px * var(--k, 1))`,
                '--zoom': isActive ? ZOOM : 1,
                // A slight tilt each, so the row reads as stickers laid down by
                // hand. The opened one straightens up.
                '--rot': `${isActive ? 0 : (tool.rot ?? 0)}deg`,
                '--delay': `${index * 90}ms`,
                zIndex: tool.z ?? tools.length - index,
              }}
              onClick={() => {
                setFocusIndex(index);
                open(index);
              }}
              onMouseEnter={() => {
                if (!isOpen) play('tap');
              }}
              aria-label={`${tool.name} — open details`}
            >
              <span
                className={styles.icon}
                // Rigged SVG from totem: animates itself and takes --gaze-x/y.
                dangerouslySetInnerHTML={{ __html: rigs[tool.id] }}
              />
              <span className={styles.tag}>{tool.name}</span>
            </button>
          );
        })}
      </div>
      </div>

      {/* Rendered only while open, so nothing invisible can intercept clicks. */}
      {active && (
        <div
          className={styles.panel}
          role="dialog"
          aria-modal="true"
          aria-label={`${active.name} details`}
          onClick={event => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <article className={styles.card} ref={cardRef}>
            <button
              type="button"
              className={styles.close}
              onClick={close}
              aria-label="Close"
            >
              &times;
            </button>

            <header className={styles.cardHeader}>
              <span
                className={styles.thumb}
                dangerouslySetInnerHTML={{ __html: rigs[active.id] }}
              />
              <h3 className={styles.cardTitle}>{active.name}</h3>
            </header>

            <p className={styles.blurb}>{active.blurb}</p>

            {/* Who the sticker is, and why it's that animal. */}
            <p className={styles.mascot}>
              <strong>{active.mascot.name}</strong> {active.mascot.text}
            </p>

            <dl className={styles.meta}>
              {Object.entries(active.meta).map(([key, value]) => (
                <div className={styles.metaRow} key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>

            <ul className={styles.tags}>
              {active.tags.map(tag => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>

            <div className={styles.cardFooter}>
              {active.href ? (
                <Button
                  secondary
                  iconHoverShift
                  iconEnd="arrowRight"
                  href={active.href}
                  className={styles.link}
                >
                  {/^https?:/.test(active.href) ? 'Source on GitHub' : 'View project'}
                </Button>
              ) : (
                // Private repos are not linked: the link would 404 for anyone else.
                <span className={styles.private}>Private</span>
              )}

              <div className={styles.paging}>
                <button type="button" onClick={() => step(-1)} aria-label="Previous">
                  &larr;
                </button>
                <button type="button" onClick={() => step(1)} aria-label="Next">
                  &rarr;
                </button>
              </div>
            </div>
          </article>
        </div>
      )}

      <div className={styles.controls}>
        <p className={styles.hint}>← → navigate · enter open · esc back</p>
        <button
          type="button"
          className={styles.mute}
          aria-pressed={muted}
          onClick={() => {
            setMuted(value => !value);
            if (muted) play('tap');
          }}
        >
          {muted ? 'sound off' : 'sound on'}
        </button>
      </div>
    </Section>
  );
}
